import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Send, Paperclip, Search, Menu, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { messagingService } from '@/services/messagingService';
import { backendCommunicationService } from '@/services/backendCommunicationService';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar'; // Assuming we have one or I'll use a placeholder div
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkipLink } from '@/components/ui/SkipLink';
import { FadeIn, SlideIn } from '@/components/ui/AnimatedComponents';
import type { JobChat, ChatMessage } from '@/types';

// Simple Avatar Component if not available
const UserAvatar = ({ name, className = "" }: { name: string, className?: string }) => (
  <div className={`rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold text-sm ${className}`}>
    {name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
  </div>
);

export function ChatPage() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { user } = useAuthStore();
  const [chats, setChats] = useState<JobChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<JobChat | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileList, setShowMobileList] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isCustomer = user?.role === 'customer';

  useEffect(() => {
    if (user) {
      const fetchChats = async () => {
        try {
          const userChats = await messagingService.getChatsForUser(user.id);
          setChats(userChats || []);

          if (chatId) {
            // Try to find chat in the list first
            let chat: JobChat | null | undefined = userChats.find(c => c.id === chatId);
            
            // If chat not found in list (e.g., just created), try to load it directly
            if (!chat) {
              try {
                chat = await messagingService.getChatById(chatId, user.id);
                if (chat) {
                  // Add it to the chats list
                  setChats(prev => {
                    const exists = prev.find(c => c.id === chatId);
                    if (!exists) {
                      return [chat!, ...prev];
                    }
                    return prev;
                  });
                }
              } catch (err) {
                // Failed to load chat
              }
            }
            
            if (chat) {
              // Ensure messages array exists (even if empty)
              const chatWithMessages = {
                ...chat,
                messages: chat.messages || []
              };
              
              setSelectedChat(chatWithMessages);
              messagingService.markMessagesAsRead(chatId, user.id);
              setShowMobileList(false); // On mobile, if chat selected, hide list
              
              // Always fetch messages to ensure we have the latest
              try {
                const messages = await backendCommunicationService.getConversationMessages(chatId);
                setSelectedChat(prev => prev ? { ...prev, messages: messages || [] } : null);
              } catch (err) {
                // Even if loading messages fails, keep the chat selected so user can still send messages
              }
            }
          } else if (userChats.length > 0 && window.innerWidth >= 768) {
            // On desktop select first chat by default
            setSelectedChat(userChats[0]);
            messagingService.markMessagesAsRead(userChats[0].id, user.id);
          }
        } catch (error) {
          // Failed to load chats
        }
      };

      fetchChats();

      const unsubscribe = messagingService.subscribe((updatedChats) => {
        setChats(updatedChats || []);
      });
      return unsubscribe;
    }
  }, [user, chatId]);

  // Auto-scroll to bottom only when new messages are added (not on every render)
  const previousMessagesLength = useRef(0);
  useEffect(() => {
    const currentLength = selectedChat?.messages?.length || 0;
    // Only scroll if messages actually increased (new message added)
    if (currentLength > previousMessagesLength.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    previousMessagesLength.current = currentLength;
  }, [selectedChat?.messages?.length]);

  // Poll for new messages when a chat is selected
  useEffect(() => {
    if (!selectedChat || !user) return;

    const pollMessages = async () => {
      try {
        const messages = await backendCommunicationService.getConversationMessages(selectedChat.id);
        setSelectedChat(prev => {
          if (!prev || prev.id !== selectedChat.id) return prev;
          return { ...prev, messages: messages || [] };
        });
      } catch (err) {
        // Failed to poll messages
      }
    };

    // Poll immediately
    pollMessages();

    // Poll every 3 seconds for new messages
    const interval = setInterval(pollMessages, 3000);

    return () => clearInterval(interval);
  }, [selectedChat?.id, user]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || !user) return;

    const messageText = newMessage.trim();
    const chatId = selectedChat.id;
    setNewMessage(''); // Clear input immediately for better UX

    // Optimistic update - add message to UI immediately
    const optimisticMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      chatId: chatId,
      senderId: user.id,
      senderName: `${user.firstName} ${user.lastName}`,
      senderRole: user.role as 'customer' | 'provider',
      message: messageText,
      messageType: 'text',
      timestamp: new Date(),
      read: false
    };

    setSelectedChat(prev => {
      if (!prev) return null;
      return {
        ...prev,
        messages: [...(prev.messages || []), optimisticMessage]
      };
    });

    try {
      // Send message to backend
      const sentMessage = await messagingService.sendMessage(
        chatId,
        user.id,
        `${user.firstName} ${user.lastName}`,
        user.role as 'customer' | 'provider',
        messageText
      );

      // Replace optimistic message with real message from backend
      setSelectedChat(prev => {
        if (!prev) return null;
        const messages = prev.messages || [];
        const filteredMessages = messages.filter(m => m.id !== optimisticMessage.id);
        return {
          ...prev,
          messages: [...filteredMessages, sentMessage]
        };
      });

      // Refetch messages to ensure we have the latest from backend (including any from other participants)
      try {
        const messages = await backendCommunicationService.getConversationMessages(chatId);
        setSelectedChat(prev => prev ? { ...prev, messages: messages || [] } : null);
      } catch (err) {
        // Keep the sent message even if refresh fails
      }
    } catch (error) {
      // Remove optimistic message on error
      setSelectedChat(prev => {
        if (!prev) return null;
        return {
          ...prev,
          messages: (prev.messages || []).filter(m => m.id !== optimisticMessage.id)
        };
      });
      // Restore message text if sending failed
      setNewMessage(messageText);
    }
  };

  const handleChatSelect = (chat: JobChat) => {
    setSelectedChat(chat);
    if (user) {
      messagingService.markMessagesAsRead(chat.id, user.id);
    }
    setShowMobileList(false);
    navigate(`/chat/${chat.id}`, { replace: true });
  };

  const getOtherParticipant = (chat: JobChat) => {
    return isCustomer
      ? { name: chat.providerBusinessName, role: 'Service Provider' }
      : { name: chat.customerName, role: 'Customer' };
  };

  const filteredChats = chats.filter(chat => {
    const other = getOtherParticipant(chat);
    return (
      chat.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      other.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', href: '/dashboard' },
              { label: 'Messages' }
            ]}
          />
        </div>
      </div>

      <main id="main-content" role="main" className="h-[calc(100vh-128px)] flex overflow-hidden">
        {/* Sidebar - Chat List */}
        <div className={`w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col ${showMobileList ? 'flex' : 'hidden md:flex'}`}>
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900 mb-4">Messages</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-primary-500 min-h-touch"
              aria-label="Search messages"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              <p>No conversations found.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredChats.map(chat => {
                const other = getOtherParticipant(chat);
                const isActive = selectedChat?.id === chat.id;
                const lastMessage = chat.lastMessage;

                return (
                  <button
                    key={chat.id}
                    onClick={() => handleChatSelect(chat)}
                    className={`w-full p-4 flex items-start text-left hover:bg-gray-50 transition-colors min-h-touch ${isActive ? 'bg-primary-50 border-l-4 border-l-primary-600' : 'border-l-4 border-l-transparent'}`}
                    aria-label={`Chat with ${other.name} about ${chat.jobTitle}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <UserAvatar name={other.name} className="w-10 h-10 flex-shrink-0 mr-3" />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className={`font-semibold text-sm truncate ${isActive ? 'text-primary-900' : 'text-gray-900'}`}>
                          {other.name}
                        </span>
                        {lastMessage && (
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {new Date(lastMessage.timestamp).toLocaleDateString() === new Date().toLocaleDateString()
                              ? formatTime(lastMessage.timestamp)
                              : new Date(lastMessage.timestamp).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-gray-500 truncate mb-1">{chat.jobTitle}</p>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600 truncate max-w-[180px]">
                          {lastMessage ? (user?.id === lastMessage.senderId ? `You: ${lastMessage.message}` : lastMessage.message) : 'Start a conversation'}
                        </p>
                        {chat.unreadCount > 0 && (
                          <span className="bg-primary-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                            {chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main - Chat Area */}
      <div className={`flex-1 flex flex-col bg-gray-50 ${!showMobileList ? 'flex' : 'hidden md:flex'}`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm z-10">
              <div className="flex items-center">
                <button
                  onClick={() => setShowMobileList(true)}
                  className="mr-4 md:hidden text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <UserAvatar name={getOtherParticipant(selectedChat).name} className="w-10 h-10 mr-3" />
                <div>
                  <h2 className="font-bold text-gray-900">{getOtherParticipant(selectedChat).name}</h2>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    {selectedChat.jobTitle}
                    <Badge variant="outline" size="sm">Active</Badge>
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              {selectedChat.messages && selectedChat.messages.length > 0 ? (
                selectedChat.messages.map((message, index) => {
                  const isMe = message.senderId === user?.id;
                  const isSystem = message.messageType === 'system';

                  if (isSystem) {
                    return (
                      <FadeIn key={message.id} delay={index * 20}>
                        <div className="flex justify-center my-4">
                          <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {message.message}
                          </span>
                        </div>
                      </FadeIn>
                    );
                  }

                  return (
                    <SlideIn 
                      key={message.id} 
                      direction={isMe ? 'right' : 'left'} 
                      delay={index * 20}
                      duration={200}
                    >
                      <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        {!isMe && (
                          <UserAvatar name={message.senderName} className="w-8 h-8 mr-2 mt-1 hidden sm:flex" />
                        )}
                        <div className={`max-w-[75%] sm:max-w-[60%] rounded-2xl px-5 py-3 shadow-sm ${isMe
                            ? 'bg-primary-600 text-white rounded-br-none'
                            : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                          }`}>
                          <p className="text-sm leading-relaxed">{message.message}</p>
                          <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-primary-100' : 'text-gray-400'}`}>
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    </SlideIn>
                  );
                })
              ) : (
                <FadeIn>
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Send className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h3>
                    <p className="text-gray-500 max-w-sm">
                      Start the conversation by sending a message below.
                    </p>
                  </div>
                </FadeIn>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-white px-4 py-4 border-t border-gray-200">
              <div className="max-w-4xl mx-auto flex items-end gap-3">
                <button 
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors min-h-touch min-w-touch" 
                  aria-label="Attach file"
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                <div className="flex-1 bg-gray-100 rounded-2xl p-2 relative">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message..."
                    className="w-full bg-transparent border-none focus:ring-0 resize-none max-h-32 min-h-[44px] py-2 px-3 text-sm text-gray-900 placeholder-gray-500"
                    rows={1}
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className={`p-3 rounded-full shadow-sm transition-colors min-h-touch min-w-touch ${newMessage.trim()
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-gray-50">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-6">
              <Send className="w-10 h-10 text-gray-400 ml-1" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Messages</h2>
            <p className="text-gray-500 max-w-sm">
              Select a conversation from the list to view chat history and send messages.
            </p>
          </div>
        )}
      </div>
      </main>
    </div>
  );
}
